import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import isoLanguages from '../../isoLanguages';
import '../../DashboardHome/brand-voice.css';
import './brandVoice.css';

const ICN = {
    x: (
        <svg viewBox="0 0 14 14" width="12" height="12" fill="none">
            <path d="M3.5 3.5l7 7M10.5 3.5l-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    plus: (
        <svg viewBox="0 0 14 14" width="13" height="13" fill="none">
            <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    check: (
        <svg viewBox="0 0 14 14" width="12" height="12" fill="none">
            <path d="M2.5 7l3 3 6-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    ban: (
        <svg viewBox="0 0 14 14" width="12" height="12" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M3.5 3.5l7 7" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    ),
};

const SUPPORTED_ADDRESS_LANGS = ['DE', 'FR', 'ES', 'IT', 'PT', 'NL', 'JA'];

const LANGS = [
    ['DE', 'German'],
    ['FR', 'French'],
    ['ES', 'Spanish'],
    ['JA', 'Japanese'],
];

const ADDRESS_LABEL = {
    DE: { formal: 'Sie · formal', casual: 'du · informal' },
    FR: { formal: 'vous', casual: 'tu' },
    ES: { formal: 'usted', casual: 'tú' },
    JA: { formal: 'desu/masu', casual: 'plain form' },
    IT: { formal: 'Lei', casual: 'tu' },
    PT: { formal: 'você (formal)', casual: 'tu' },
    NL: { formal: 'u', casual: 'jij' },
};

const SAMPLES = {
    DE: [
        {
            src: 'Welcome back! Ready to pick up where you left off?',
            generic: 'Willkommen zurück! Bereit, dort weiterzumachen, wo Sie aufgehört haben?',
            formal: 'Schön, dass Sie wieder da sind. Möchten Sie dort weitermachen, wo Sie aufgehört haben?',
            casual: 'Schön, dass du wieder da bist. Lust, da weiterzumachen, wo du aufgehört hast?',
        },
        {
            src: 'Your free trial ends soon. Upgrade to keep your projects.',
            generic: 'Ihre kostenlose Testversion endet bald. Führen Sie ein Upgrade durch, um Ihre Projekte zu behalten.',
            formal: 'Ihre Testphase endet in Kürze — sichern Sie sich den vollen Zugang zu Ihren Projekten.',
            casual: 'Deine Testphase endet bald — sichere dir den vollen Zugang zu deinen Projekten.',
        },
        {
            src: 'Oops — something went wrong. Try again.',
            generic: 'Hoppla – etwas ist schiefgelaufen. Versuchen Sie es erneut.',
            formal: 'Da ist etwas schiefgelaufen. Bitte versuchen Sie es noch einmal.',
            casual: 'Da ist was schiefgelaufen. Versuch es einfach nochmal.',
        },
    ],
    FR: [
        {
            src: 'Welcome back! Ready to pick up where you left off?',
            generic: 'Bienvenue à nouveau ! Prêt à reprendre là où vous vous êtes arrêté ?',
            formal: 'Ravis de vous revoir. Souhaitez-vous reprendre là où vous vous êtes arrêté ?',
            casual: 'Content de te revoir ! Envie de reprendre là où tu t\'es arrêté ?',
        },
        {
            src: 'Your free trial ends soon. Upgrade to keep your projects.',
            generic: 'Votre essai gratuit se termine bientôt. Effectuez une mise à niveau pour conserver vos projets.',
            formal: 'Votre période d\'essai se termine bientôt — conservez l\'accès à tous vos projets.',
            casual: 'Ton essai se termine bientôt — garde l\'accès à tous tes projets.',
        },
        {
            src: 'Oops — something went wrong. Try again.',
            generic: 'Oups – une erreur s\'est produite. Veuillez réessayer.',
            formal: 'Une erreur s\'est produite. Veuillez réessayer.',
            casual: 'Aïe, ça n\'a pas marché. Réessaie.',
        },
    ],
    ES: [
        {
            src: 'Welcome back! Ready to pick up where you left off?',
            generic: '¡Bienvenido de nuevo! ¿Listo para continuar donde lo dejaste?',
            formal: 'Nos alegra verle de nuevo. ¿Desea continuar donde lo dejó?',
            casual: '¡Qué bueno verte! ¿Sigues donde lo dejaste?',
        },
    ],
    JA: [
        {
            src: 'Welcome back!',
            generic: 'おかえりなさい！',
            formal: 'お帰りなさいませ。',
            casual: 'おかえり。',
        },
    ],
};

const Spark = () => (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
        <path d="M8 2l1.3 3.4L13 6.6 9.7 8 8 11.5 6.3 8 3 6.6l3.4-1.2L8 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
);

const RefreshI = () => (
    <svg viewBox="0 0 14 14" width="13" height="13" fill="none">
        <path d="M11.5 3.5a5 5 0 10.8 4.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M11.5 1.5v2.5H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Per-card subtitle: a single line that names the field's effect and which
// engines respect it. Keep these tight — verbosity here just adds page weight.
const CardSub = ({ children, badge }) => (
    <p style={{ color: 'var(--ink-3)', marginTop: 0, marginBottom: 12, fontSize: 13, lineHeight: 1.5 }}>
        {badge && (
            <span
                style={{
                    display: 'inline-block',
                    marginRight: 8,
                    padding: '2px 7px',
                    borderRadius: 999,
                    border: '1px solid var(--line)',
                    background: 'var(--bg)',
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--ink-2, var(--ink))',
                    verticalAlign: 1,
                }}
            >
                {badge}
            </span>
        )}
        {children}
    </p>
);

// Compact explainer strip. The page is full of knobs — this is the one place
// we set expectations: what the profile actually does, what every engine
// respects, and what the order of precedence is when rules conflict.
const HowItWorks = () => {
    const chipStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        border: '1px solid var(--line)',
        background: 'var(--bg)',
        color: 'var(--ink-2, var(--ink))',
        fontSize: 12,
        whiteSpace: 'nowrap',
    };
    const dot = (color) => (
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'inline-block' }} />
    );
    return (
        <div
            className="rs-card"
            style={{
                margin: '8px 0 14px',
                padding: '12px 14px',
                background: 'var(--bg)',
                border: '1px solid var(--line)',
            }}
        >
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ marginTop: 2, color: 'var(--accent, #4a90e2)' }}><Spark /></span>
                <div style={{ flex: 1, fontSize: 13, color: 'var(--ink-2, var(--ink))', lineHeight: 1.55 }}>
                    Brand voice shapes <b>every AI-drafted translation</b> in this workspace. Tone, pronoun
                    register, and reading level steer the language model. Do-not-translate terms and preferred
                    word swaps are enforced on every engine — including DeepL and Google. Forbidden terms also
                    raise warnings on the AI Quality dashboard so reviewers can catch them.
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                        <span style={chipStyle}>{dot('#7bd389')}Applied to every AI draft</span>
                        <span style={chipStyle}>{dot('#f3b54a')}DeepL / Google honor DNT + preferred only</span>
                        <span style={chipStyle}>{dot('#9b8cf6')}Glossary wins on conflict</span>
                        <span style={chipStyle}>{dot('#6cc4f1')}Shared across your team</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const TryVoice = ({ activeLang, voice, token, languages }) => {
    const [idx, setIdx] = useState(0);
    const samples = SAMPLES[activeLang] || SAMPLES.DE;
    const [live, setLive] = useState(null);   // { generic, onBrand } when fetched
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    // Auto-regenerate is opt-in: it turns on after the first manual
    // Regenerate click. That makes the OpenAI cost an explicit consent.
    const [autoOn, setAutoOn] = useState(false);

    useEffect(() => { setIdx(0); setLive(null); setErr(null); }, [activeLang]);
    useEffect(() => { setLive(null); }, [idx]);

    const s = samples[idx] || samples[0];
    const addr = (voice.address || {})[activeLang] || 'formal';
    const fallbackOnBrand = addr === 'casual' ? s.casual : s.formal;
    const langName = (languages.find((l) => l[0] === activeLang) || LANGS.find((l) => l[0] === activeLang) || [])[1] || activeLang;
    const addrLabel = ADDRESS_LABEL[activeLang]?.[addr] || addr;

    const regenerate = async ({ enableAuto = true } = {}) => {
        setLoading(true);
        setErr(null);
        try {
            const { _id, userId, __v, updatedAt, ...payload } = voice;
            const r = await axios.post(
                '/api/brand-voice/preview',
                { source: s.src, targetLang: activeLang, voice: payload },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setLive(r.data || null);
            if (enableAuto) setAutoOn(true);
        } catch (e) {
            setErr(e.response?.data?.msg || 'Could not generate live preview.');
        } finally {
            setLoading(false);
        }
    };

    // Auto-regenerate: when on, fire ~800ms after the user stops editing.
    // Signature keys only on fields that compile into the prompt — typing
    // into Voice summary won't fire until they stop, and unrelated edits
    // (forbidden/preferred lists) still trigger because they change the
    // prompt's hard-rule footer too.
    const profileSignature = JSON.stringify({
        s: voice.summary || '',
        t: voice.tone || {},
        a: (voice.address || {})[activeLang] || 'formal',
        r: voice.reading,
        l: voice.length,
        d: voice.doNotTranslate || [],
        f: voice.forbidden || [],
        p: voice.preferred || [],
        e: voice.examples || [],
        src: s.src,
        lang: activeLang,
    });
    useEffect(() => {
        if (!autoOn) return undefined;
        const t = setTimeout(() => {
            // Skip the auto-enable on programmatic fires so we don't latch
            // ourselves into a refresh loop on the first re-render.
            regenerate({ enableAuto: false });
        }, 800);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileSignature, autoOn]);

    const genericText = live?.generic || s.generic;
    const onBrandText = live?.onBrand || fallbackOnBrand;

    // "What changed" — derive a short list of the rules actually applied to
    // this preview from the current profile. This is the bridge between the
    // abstract knobs above and the concrete output below.
    // Mirror the bands used in src/lib/brandVoice/compile.js so the chips
    // accurately reflect what the model receives.
    const formalityChip = (n) => (
        n >= 80 ? 'Highly formal' :
        n >= 60 ? 'Formal register' :
        n >= 45 ? 'Mildly formal' :
        n >= 30 ? 'Relaxed register' :
        n >= 15 ? 'Casual register' : 'Very casual'
    );
    const warmthChip = (n) => (
        n >= 75 ? 'Warm tone' :
        n >= 55 ? 'Mildly warm' :
        n >= 35 ? 'Neutral warmth' :
        n >= 15 ? 'Cool tone' : 'Detached tone'
    );
    const playChip = (n) => (
        n >= 75 ? 'Playful' :
        n >= 55 ? 'Mildly playful' :
        n >= 35 ? 'Restrained' :
        n >= 15 ? 'Serious' : 'Strictly serious'
    );
    const energyChip = (n) => (
        n >= 75 ? 'Energetic' :
        n >= 55 ? 'Mildly energetic' :
        n >= 35 ? 'Balanced pacing' :
        n >= 15 ? 'Calm pacing' : 'Slow pacing'
    );
    const applied = (() => {
        const out = [];
        out.push(`Address: ${addrLabel}`);
        out.push(formalityChip(Number(voice.tone?.formality ?? 50)));
        out.push(warmthChip(Number(voice.tone?.warmth ?? 50)));
        out.push(playChip(Number(voice.tone?.playfulness ?? 50)));
        out.push(energyChip(Number(voice.tone?.energy ?? 50)));
        out.push(`Reading: ${voice.reading || 'standard'}`);
        out.push(`Length: ${voice.length || 'balanced'}`);
        const dnt = (voice.doNotTranslate || []).length;
        if (dnt) out.push(`${dnt} DNT term${dnt > 1 ? 's' : ''} preserved`);
        const pref = (voice.preferred || []).length;
        if (pref) out.push(`${pref} preferred swap${pref > 1 ? 's' : ''}`);
        const ex = (voice.examples || []).length;
        if (ex) out.push(`${ex} example${ex > 1 ? 's' : ''} as anchors`);
        if (voice.summary && voice.summary.trim()) out.push('Custom voice summary');
        return out;
    })();

    return (
        <div className="bv-try">
            <div className="bv-try-h">
                <span className="bv-try-ic"><Spark /></span>
                <div>
                    <b>Try the voice</b>
                    <span>
                        {autoOn
                            ? (loading ? 'Updating live preview…' : 'Live preview · auto-updates as you edit.')
                            : (live
                                ? 'Live preview using your current (unsaved) profile.'
                                : 'See how your profile shapes translations — compared to a generic engine.')}
                    </span>
                </div>
                <div className="grow" />
                {autoOn && (
                    <label
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 12,
                            color: 'var(--ink-3)',
                            marginRight: 10,
                            cursor: 'pointer',
                        }}
                        title="Stop auto-regenerating on every edit."
                    >
                        <input
                            type="checkbox"
                            checked={autoOn}
                            onChange={(e) => setAutoOn(e.target.checked)}
                        />
                        Auto-update
                    </label>
                )}
                <button type="button" className="set-btn sm" onClick={() => regenerate()} disabled={loading}>
                    <RefreshI /> {loading ? 'Generating…' : (autoOn ? 'Regenerate now' : 'Regenerate')}
                </button>
            </div>

            <div className="bv-try-samples">
                {samples.map((sm, i) => (
                    <button
                        type="button"
                        key={i}
                        className={`bv-sample ${i === idx ? 'on' : ''}`}
                        onClick={() => setIdx(i)}
                    >
                        {sm.src}
                    </button>
                ))}
            </div>

            {err && (
                <div style={{ color: 'crimson', fontSize: 12.5, padding: '6px 2px' }}>{err}</div>
            )}

            <div className="bv-try-out">
                <div className="bv-out generic">
                    <div className="bv-out-h"><span className="tag">Generic engine</span></div>
                    <div className="bv-out-t">{genericText}</div>
                </div>
                <div className="bv-out brand">
                    <div className="bv-out-h">
                        <span className="tag accent"><Spark /> On-brand · Rush AI</span>
                    </div>
                    <div className="bv-out-t">{onBrandText}</div>
                    <div
                        style={{
                            marginTop: 8,
                            fontSize: 11.5,
                            color: 'var(--ink-3)',
                            textTransform: 'uppercase',
                            letterSpacing: 0.4,
                        }}
                    >
                        Applied to this draft
                    </div>
                    <div className="bv-out-chips">
                        {applied.map((label) => (
                            <span className="ac" key={label}>{label}</span>
                        ))}
                        <span className="ac" style={{ opacity: 0.7 }}>{langName} profile</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const LangBar = ({ active, onChange, languages, addable, onAdd }) => {
    const [picking, setPicking] = useState(false);
    return (
        <div className="bv-langbar" style={{ position: 'relative' }}>
            <span className="bv-langbar-l">Voice profile for</span>
            {languages.map(([code, name]) => (
                <button
                    type="button"
                    key={code}
                    className={`bv-lang ${active === code ? 'on' : ''}`}
                    onClick={() => onChange(code)}
                >
                    <span className="code">{code}</span>{name}
                </button>
            ))}
            {addable.length > 0 && (
                <>
                    <button type="button" className="bv-lang add" onClick={() => setPicking((p) => !p)}>
                        + Add language
                    </button>
                    {picking && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 'calc(100% + 6px)',
                                right: 0,
                                background: 'var(--card)',
                                border: '1px solid var(--line)',
                                borderRadius: 10,
                                boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
                                padding: 6,
                                zIndex: 20,
                                minWidth: 180,
                            }}
                        >
                            {addable.map(([code, name]) => (
                                <button
                                    type="button"
                                    key={code}
                                    onClick={() => { onAdd(code); setPicking(false); }}
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        textAlign: 'left',
                                        padding: '8px 10px',
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        borderRadius: 6,
                                        color: 'var(--ink)',
                                        fontSize: 13,
                                    }}
                                >
                                    <span style={{ fontWeight: 600, marginRight: 8 }}>{code}</span>{name}
                                </button>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

const ToneSlider = ({ label, left, right, value, onChange }) => (
    <div className="bv-tone">
        <div className="bv-tone-h">
            <span className="lbl">{label}</span>
            <span className="val">{value}</span>
        </div>
        <div className="bv-tone-row">
            <span className="end">{left}</span>
            <input
                type="range"
                className="set-slider"
                min={0}
                max={100}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
            />
            <span className="end">{right}</span>
        </div>
    </div>
);

const ChipInput = ({ items, onAdd, onRemove, placeholder }) => {
    const [v, setV] = useState('');
    const add = () => {
        const x = v.trim();
        if (x && !items.includes(x)) onAdd(x);
        setV('');
    };
    return (
        <div className="bv-chips">
            {items.map((it) => (
                <span key={it} className="bv-chip">
                    {it}
                    <button type="button" onClick={() => onRemove(it)} aria-label={`Remove ${it}`}>{ICN.x}</button>
                </span>
            ))}
            <input
                className="bv-chip-input"
                value={v}
                placeholder={placeholder}
                onChange={(e) => setV(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            />
        </div>
    );
};

const PairList = ({ items, onAdd, onUpdate, onRemove, labels }) => {
    const [a, setA] = useState('');
    const [b, setB] = useState('');
    return (
        <div className="bv-pairs">
            {items.map((it, i) => (
                <div className="bv-pair" key={i}>
                    <input value={it[labels[0]]} onChange={(e) => onUpdate(i, labels[0], e.target.value)} />
                    <span className="arrow">→</span>
                    <input value={it[labels[1]]} onChange={(e) => onUpdate(i, labels[1], e.target.value)} />
                    <button type="button" onClick={() => onRemove(i)} aria-label="Remove">{ICN.x}</button>
                </div>
            ))}
            <div className="bv-pair">
                <input value={a} onChange={(e) => setA(e.target.value)} placeholder={labels[2]} />
                <span className="arrow">→</span>
                <input value={b} onChange={(e) => setB(e.target.value)} placeholder={labels[3]} />
                <button
                    type="button"
                    onClick={() => {
                        if (!a.trim() || !b.trim()) return;
                        onAdd({ [labels[0]]: a.trim(), [labels[1]]: b.trim() });
                        setA('');
                        setB('');
                    }}
                >
                    {ICN.plus}
                </button>
            </div>
        </div>
    );
};

const BrandVoice = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [voice, setVoice] = useState(null);
    const [savedFlash, setSavedFlash] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [activeLang, setActiveLang] = useState('DE');
    // The set of language tabs visible in the LangBar — starts from the
    // saved address map (so previously-added languages stick) plus the
    // built-in default set.
    const [activeLangs, setActiveLangs] = useState(LANGS.map(([c]) => c));

    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/brand-voice', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                setVoice(r.data);
                // Hydrate visible language tabs from saved address map so
                // previously-added languages survive a reload.
                const saved = Object.keys((r.data && r.data.address) || {})
                    .map((c) => c.toUpperCase())
                    .filter((c) => SUPPORTED_ADDRESS_LANGS.includes(c));
                setActiveLangs((cur) => {
                    const merged = [...cur];
                    saved.forEach((c) => { if (!merged.includes(c)) merged.push(c); });
                    return merged;
                });
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load brand voice.');
                    setLoading(false);
                }
            });
    }, [token, dispatch, navigate]);

    const update = (patch) => setVoice((v) => ({ ...v, ...patch }));
    const updateTone = (k, val) => setVoice((v) => ({ ...v, tone: { ...v.tone, [k]: val } }));
    const updateAddress = (lang, val) => setVoice((v) => ({ ...v, address: { ...(v.address || {}), [lang]: val } }));

    const save = async () => {
        setSaving(true);
        try {
            const { _id, userId, __v, updatedAt, ...payload } = voice;
            await axios.put('/api/brand-voice', payload, { headers: { Authorization: `Bearer ${token}` } });
            setSavedFlash(true);
            setTimeout(() => setSavedFlash(false), 1500);
        } catch (e) {
            setError('Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    const languageOptions = useMemo(() => {
        const codes = new Set(SUPPORTED_ADDRESS_LANGS);
        return isoLanguages.filter((l) => codes.has(l.code.toUpperCase())).map((l) => ({ code: l.code.toUpperCase(), name: l.name }));
    }, []);

    // Pair active codes with their display names for LangBar; codes not yet
    // visible become the picker's "addable" list.
    const nameOf = (code) => (languageOptions.find((o) => o.code === code) || {}).name
        || (LANGS.find((l) => l[0] === code) || [])[1]
        || code;
    const langTabs = activeLangs.map((c) => [c, nameOf(c)]);
    const addableLangs = SUPPORTED_ADDRESS_LANGS
        .filter((c) => !activeLangs.includes(c))
        .map((c) => [c, nameOf(c)]);

    const addLangTab = (code) => {
        setActiveLangs((cur) => (cur.includes(code) ? cur : [...cur, code]));
        // Seed an address default so the new tab has something coherent.
        if (!((voice.address || {})[code])) updateAddress(code, 'formal');
        setActiveLang(code);
    };

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading brand voice…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;
    if (!voice) return null;

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">Brand voice</h1>
                <div className="rs-page-actions">
                    {savedFlash && <span style={{ color: 'var(--green)', fontWeight: 600, fontSize: 13 }}>Saved ✓</span>}
                    <button type="button" className="btn-acc" onClick={save} disabled={saving}>
                        {saving ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            </div>
            <p className="rs-page-sub">Define how your brand sounds in each language. Rush AI applies this profile to every draft.</p>

            <HowItWorks />

            <LangBar
                active={activeLang}
                onChange={setActiveLang}
                languages={langTabs}
                addable={addableLangs}
                onAdd={addLangTab}
            />

            <TryVoice activeLang={activeLang} voice={voice} token={token} languages={langTabs} />

            <div className="bv-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
                <div className="rs-card">
                    <div className="rs-card-h"><h3>Voice summary</h3></div>
                    <CardSub badge="OpenAI only">
                        One or two sentences describing how your brand sounds. Passed verbatim to the language model as a top-level brief — the most direct way to nudge style.
                    </CardSub>
                    <textarea
                        value={voice.summary || ''}
                        onChange={(e) => update({ summary: e.target.value })}
                        placeholder="Describe how your brand should sound in one or two sentences…"
                        style={{
                            width: '100%',
                            minHeight: 80,
                            border: '1px solid var(--line)',
                            borderRadius: 10,
                            padding: 12,
                            background: 'var(--bg)',
                            color: 'var(--ink)',
                            fontFamily: 'inherit',
                            fontSize: 13.5,
                            outline: 'none',
                            resize: 'vertical',
                        }}
                    />
                </div>

                <div className="rs-card">
                    <div className="rs-card-h"><h3>Tone</h3></div>
                    <CardSub badge="OpenAI only">
                        Each slider becomes a phrasing instruction in the AI prompt — e.g. "use a formal register, no contractions" or "warm and human; acknowledge the reader." DeepL and Google can't read tone, so they ignore these.
                    </CardSub>
                    <ToneSlider label="Formality" left="Casual" right="Formal" value={voice.tone.formality} onChange={(v) => updateTone('formality', v)} />
                    <ToneSlider label="Warmth" left="Cool" right="Warm" value={voice.tone.warmth} onChange={(v) => updateTone('warmth', v)} />
                    <ToneSlider label="Playfulness" left="Serious" right="Playful" value={voice.tone.playfulness} onChange={(v) => updateTone('playfulness', v)} />
                    <ToneSlider label="Energy" left="Calm" right="Energetic" value={voice.tone.energy} onChange={(v) => updateTone('energy', v)} />
                </div>

                <div className="rs-card">
                    <div className="rs-card-h"><h3>Address by language</h3></div>
                    <CardSub badge="OpenAI only">
                        Sets the pronoun register for languages with a formal/informal split (Sie vs du, vous vs tu, usted vs tú, desu/masu vs plain). Applied per language — pick once, every draft in that target language follows.
                    </CardSub>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                        {languageOptions.map((l) => {
                            const v = (voice.address || {})[l.code] || 'formal';
                            return (
                                <div key={l.code} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ minWidth: 60, fontWeight: 600 }}>{l.code}</span>
                                    <span style={{ flex: 1, color: 'var(--ink-3)', fontSize: 12.5 }}>{l.name}</span>
                                    <div className="bv-seg">
                                        <button type="button" className={v === 'formal' ? 'on' : ''} onClick={() => updateAddress(l.code, 'formal')}>Formal</button>
                                        <button type="button" className={v === 'casual' ? 'on' : ''} onClick={() => updateAddress(l.code, 'casual')}>Casual</button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="rs-card">
                    <div className="rs-card-h"><h3>Reading level &amp; length</h3></div>
                    <CardSub badge="OpenAI only">
                        <b>Reading level</b> nudges vocabulary complexity (Simple ≈ CEFR B1; Technical preserves domain terms). <b>Length</b> permits or trims expansion — useful for UI copy that has to fit a button.
                    </CardSub>
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 100%' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Reading level</div>
                            <div className="bv-seg" style={{ flexWrap: 'wrap' }}>
                                {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map((r) => (
                                    <button
                                        type="button"
                                        key={r}
                                        className={voice.reading === r ? 'on' : ''}
                                        onClick={() => update({ reading: r })}
                                        title={`CEFR ${r}`}
                                    >
                                        {r}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    className={(!voice.reading || voice.reading === 'standard' || voice.reading === 'simple') ? (voice.reading === 'standard' || !voice.reading ? 'on' : '') : ''}
                                    onClick={() => update({ reading: 'standard' })}
                                    title="Match the source's reading level"
                                >
                                    Match source
                                </button>
                                <button
                                    type="button"
                                    className={voice.reading === 'technical' ? 'on' : ''}
                                    onClick={() => update({ reading: 'technical' })}
                                    title="Preserve domain vocabulary even at lower reading levels"
                                >
                                    Technical
                                </button>
                            </div>
                            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 6 }}>
                                {voice.reading === 'simple' && 'Plain language, ~B1. (Legacy value — use B1 directly for new profiles.)'}
                                {voice.reading === 'technical' && 'Domain vocabulary preserved, even if the source uses jargon.'}
                                {(voice.reading === 'standard' || !voice.reading) && 'No simplification or elaboration — the translation tracks the source.'}
                                {voice.reading === 'A1' && 'Beginner reading — ~500 most common words, very short sentences.'}
                                {voice.reading === 'A2' && 'Elementary reading — everyday vocabulary, short sentences.'}
                                {voice.reading === 'B1' && 'Intermediate reading — common vocabulary, moderate sentences.'}
                                {voice.reading === 'B2' && 'Upper-intermediate — broader vocabulary, complex sentences allowed.'}
                                {voice.reading === 'C1' && 'Advanced — rich vocabulary including idioms and figurative language.'}
                                {voice.reading === 'C2' && 'Proficient — full native-level range including literary vocabulary.'}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Length</div>
                            <div className="bv-seg">
                                {['terse', 'balanced', 'expansive'].map((r) => (
                                    <button type="button" key={r} className={voice.length === r ? 'on' : ''} onClick={() => update({ length: r })}>
                                        {r}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="rs-card">
                    <div className="rs-card-h"><h3>Do not translate</h3></div>
                    <CardSub badge="Hard rule · every engine">
                        Brand names, product names, codes. Restored verbatim — with original casing — after every AI draft, including DeepL and Google. If a draft alters one, the AI Quality dashboard flags the segment.
                    </CardSub>
                    <ChipInput
                        items={voice.doNotTranslate || []}
                        onAdd={(x) => update({ doNotTranslate: [...(voice.doNotTranslate || []), x] })}
                        onRemove={(x) => update({ doNotTranslate: (voice.doNotTranslate || []).filter((i) => i !== x) })}
                        placeholder="Type and press Enter"
                    />
                </div>

                <div className="rs-card">
                    <div className="rs-card-h"><h3>Forbidden terms</h3></div>
                    <CardSub badge="Quality warning · every engine">
                        Words you never want to ship. We tell the AI to avoid them, and if one slips through any engine, it surfaces as a <i>terminology</i> warning on AI Quality with your authored reason attached.
                    </CardSub>
                    <PairList
                        items={voice.forbidden || []}
                        onAdd={(p) => update({ forbidden: [...(voice.forbidden || []), p] })}
                        onUpdate={(i, k, val) => {
                            const next = [...(voice.forbidden || [])];
                            next[i] = { ...next[i], [k]: val };
                            update({ forbidden: next });
                        }}
                        onRemove={(i) => update({ forbidden: (voice.forbidden || []).filter((_, idx) => idx !== i) })}
                        labels={['term', 'reason', 'Term to avoid', 'Why / what to use instead']}
                    />
                </div>

                <div className="rs-card">
                    <div className="rs-card-h"><h3>Preferred terms</h3></div>
                    <CardSub badge="Hard rule · every engine">
                        Whole-word, case-aware substitution applied after every draft (DeepL, Google, OpenAI). Replace <i>"purchase" → "buy"</i> once; it sticks across the workspace.
                    </CardSub>
                    <PairList
                        items={voice.preferred || []}
                        onAdd={(p) => update({ preferred: [...(voice.preferred || []), p] })}
                        onUpdate={(i, k, val) => {
                            const next = [...(voice.preferred || [])];
                            next[i] = { ...next[i], [k]: val };
                            update({ preferred: next });
                        }}
                        onRemove={(i) => update({ preferred: (voice.preferred || []).filter((_, idx) => idx !== i) })}
                        labels={['avoid', 'use', 'Avoid', 'Prefer']}
                    />
                </div>

                <ExamplesEditor
                    items={voice.examples || []}
                    onChange={(next) => update({ examples: next })}
                />
            </div>
        </>
    );
};

const ExamplesEditor = ({ items, onChange }) => {
    const [src, setSrc] = useState('');
    const [good, setGood] = useState('');
    const [avoid, setAvoid] = useState('');

    const add = () => {
        const s = src.trim();
        const g = good.trim();
        const a = avoid.trim();
        if (!s || (!g && !a)) return;
        onChange([...items, { source: s, good: g, avoid: a }]);
        setSrc(''); setGood(''); setAvoid('');
    };

    const updateAt = (i, k, val) => {
        const next = items.map((it, idx) => (idx === i ? { ...it, [k]: val } : it));
        onChange(next);
    };
    const removeAt = (i) => onChange(items.filter((_, idx) => idx !== i));

    const inputStyle = {
        width: '100%',
        border: '1px solid var(--line)',
        borderRadius: 8,
        padding: '8px 10px',
        background: 'var(--bg)',
        color: 'var(--ink)',
        fontSize: 13,
        outline: 'none',
    };

    return (
        <div className="rs-card">
            <div className="rs-card-h"><h3>Example translations</h3></div>
            <CardSub badge="OpenAI only">
                Few-shot anchors. Each pair is included in the AI prompt so the model can match real on-brand phrasing — usually the highest-leverage field if your voice is hard to describe in sliders. Add a <b>good</b> rendering, an <b>avoid</b> rendering, or both.
            </CardSub>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {items.map((it, i) => (
                    <div
                        key={i}
                        style={{
                            border: '1px solid var(--line)',
                            borderRadius: 10,
                            padding: 10,
                            display: 'grid',
                            gridTemplateColumns: '1fr',
                            gap: 8,
                            position: 'relative',
                        }}
                    >
                        <input
                            value={it.source || ''}
                            placeholder="Source"
                            onChange={(e) => updateAt(i, 'source', e.target.value)}
                            style={inputStyle}
                        />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <input
                                value={it.good || ''}
                                placeholder="Good (on-brand)"
                                onChange={(e) => updateAt(i, 'good', e.target.value)}
                                style={inputStyle}
                            />
                            <input
                                value={it.avoid || ''}
                                placeholder="Avoid"
                                onChange={(e) => updateAt(i, 'avoid', e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => removeAt(i)}
                            aria-label="Remove example"
                            style={{
                                position: 'absolute',
                                top: 6,
                                right: 6,
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                color: 'var(--ink-3)',
                            }}
                        >
                            {ICN.x}
                        </button>
                    </div>
                ))}

                <div
                    style={{
                        border: '1px dashed var(--line)',
                        borderRadius: 10,
                        padding: 10,
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        gap: 8,
                    }}
                >
                    <input
                        value={src}
                        placeholder="Source"
                        onChange={(e) => setSrc(e.target.value)}
                        style={inputStyle}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8 }}>
                        <input
                            value={good}
                            placeholder="Good (on-brand)"
                            onChange={(e) => setGood(e.target.value)}
                            style={inputStyle}
                        />
                        <input
                            value={avoid}
                            placeholder="Avoid"
                            onChange={(e) => setAvoid(e.target.value)}
                            style={inputStyle}
                        />
                        <button
                            type="button"
                            onClick={add}
                            className="set-btn sm"
                            disabled={!src.trim() || (!good.trim() && !avoid.trim())}
                        >
                            {ICN.plus}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BrandVoice;
